import type { ContainerProps } from "@mantine/core";
import { Box, Button, Stack, Text, Container } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RenderJson } from "octalog-base";
import type { ReactNode } from "react";
import { useState } from "react";

import { QueryLoaderBoundary } from "../../../components/loader/QueryLoaderBoundary";
import type { ExternalSystemSettingsResponseDto } from "../../../generated/client/octalog-service";
import { ExternalSystem as ExternalSystemEnum } from "../../../generated/client/octalog-service";
import {
  deleteExternalSystemSettingsMutation,
  triggerSyncMutation,
  listExternalSystemsSettingsOptions,
  listExternalSystemsSettingsQueryKey,
} from "../../../generated/client/octalog-service/@tanstack/react-query.gen";
import { useBaseMutation } from "../../../hooks/useBaseMutation";

import { ExternalSystemSettingItem } from "./ExternalSystemSettingItem";
import { ExternalSystemSettingsFormModal } from "./ExternalSystemSettingsFormModal";

export function ExternalSystemSettings(props: ContainerProps): ReactNode {
  const listSettingsQuery = useQuery(listExternalSystemsSettingsOptions());

  const queryClient = useQueryClient();
  const [addEditModalOpened, { open: openAddEditModal, close: closeAddEditModal }] = useDisclosure(false);
  const [editingSetting, setEditingSetting] = useState<ExternalSystemSettingsResponseDto | null>(null);

  function handleEdit(setting: ExternalSystemSettingsResponseDto) {
    setEditingSetting(setting);
    openAddEditModal();
  }

  const deleteMutationOptions = useBaseMutation({
    ...deleteExternalSystemSettingsMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listExternalSystemsSettingsQueryKey() });
    },
    successMessage: "Externe System-Einstellung erfolgreich gelöscht.",
    errorMessage: "Fehler beim Löschen der externen System-Einstellung.",
  });

  const deleteMutation = useMutation(deleteMutationOptions);

  const syncMutationOptions = useBaseMutation({
    ...triggerSyncMutation(),
    successMessage: (response) => {
      const result = response.data;
      return result.hasFailures
        ? `Synchronisation abgeschlossen: ${result.successCount} erfolgreich, ${result.failureCount} fehlgeschlagen (${result.totalCount} gesamt)`
        : `Synchronisation erfolgreich: ${result.successCount} Kontakte synchronisiert`;
    },
    errorMessage: "Fehler beim Starten der Kontaktsynchronisation.",
  });

  const syncMutation = useMutation(syncMutationOptions);

  function handleDelete(settingId: string) {
    // noinspection JSUnusedGlobalSymbols
    modals.openConfirmModal({
      title: "Einstellung löschen",
      children: <Text size="sm">Möchten Sie diese externe System-Einstellung wirklich löschen?</Text>,
      labels: { confirm: "Löschen", cancel: "Abbrechen" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate({ path: { id: settingId } });
      },
      onCancel: () => {
        // No action on cancel
      },
    });
  }

  function handleTriggerSync(setting: ExternalSystemSettingsResponseDto): void {
    // Only allow sync for Lexware
    if (setting.system === ExternalSystemEnum.LEXWARE) {
      syncMutation.mutate({});
    } else {
      console.warn("Sync triggered for non-Lexware system:", setting.system);
    }
  }

  function handleOpenAddModal(): void {
    setEditingSetting(null);
    openAddEditModal();
  }

  return (
    <Container {...props}>
      <QueryLoaderBoundary queryResult={listSettingsQuery}>
        {(data) => {
          const settings = data.data;

          return (
            <Stack gap="md" mb="md">
              {settings.length > 0 ? (
                settings.map((setting) => (
                  <ExternalSystemSettingItem
                    key={setting.id}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onTriggerSync={handleTriggerSync}
                    setting={setting}
                  />
                ))
              ) : (
                <Box mt="md" style={{ display: "flex", justifyContent: "center" }}>
                  <Button onClick={handleOpenAddModal} variant="light">
                    <IconPlus size={16} />
                  </Button>
                </Box>
              )}
              <RenderJson data={settings} />
            </Stack>
          );
        }}
      </QueryLoaderBoundary>

      <ExternalSystemSettingsFormModal
        initialData={editingSetting}
        onClose={closeAddEditModal}
        opened={addEditModalOpened}
      />
    </Container>
  );
}
